// Costos logísticos por proveedor (pedido de Rodrigo, 2026-10-20). Cómo cobra el flete cada proveedor:
//  - en la línea del producto (ese flete es de ese producto), o
//  - en un total de la factura, repartido por valor, por unidades o por el COSTO LOGÍSTICO que el
//    proveedor informa por producto (CCU y otros: se le pide la lista y se carga aquí).
// La regla del proveedor queda como su política de flete; los costos, en un libro con vigencia.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { decimal, fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { estimar, hoy } from './comun';
import { useAccionCompras } from './datos';

interface FilaCosto {
  productoId: string;
  sku: string | null;
  descripcion: string | null;
  presentacionCodigo: string | null;
  factorABase: string | null;
  montoBase: string | null;
  vigenteDesde: string | null;
  /** De dónde salió: la lista del proveedor (a mano) o «Factura N° … (automático)». */
  motivo?: string | null;
  ultimaCompra: string | null;
}

type Cobro = 'EN_LINEA' | 'FLETE_POR_VALOR' | 'FLETE_POR_UNIDADES' | 'FLETE_POR_COSTO_LOGISTICO';

const COBROS: Array<[Cobro, string, string]> = [
  [
    'EN_LINEA',
    'En la línea del producto',
    'Cada producto trae su flete: es de ese producto. Al aceptar la factura, ese flete por unidad queda como su costo logístico (la lista se actualiza sola y se puede corregir a mano).',
  ],
  [
    'FLETE_POR_COSTO_LOGISTICO',
    'En un total, por costo logístico',
    'El total se reparte por unidades × el costo logístico de cada producto (la lista de abajo).',
  ],
  [
    'FLETE_POR_VALOR',
    'En un total, por valor',
    'El total se reparte según el neto de cada producto.',
  ],
  [
    'FLETE_POR_UNIDADES',
    'En un total, por unidades',
    'El total se reparte por unidades recibidas.',
  ],
];

/** «76123456» → código de la política de flete del proveedor. */
const codigoPolitica = (rut: string | null, id: string) =>
  `FLETE_P_${(rut ?? id.replace(/-/g, '').slice(0, 12)).replace(/\D/g, '') || id.slice(0, 8).toUpperCase()}`;

export function CostosLogisticos({
  proveedorFijo,
}: {
  /** Desde la ficha del proveedor: ya elegido y sin buscador. */
  proveedorFijo?: { id: string; rut: string | null; razon: string } | undefined;
} = {}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const accion = useAccionCompras(empresaId);
  const [elegido, setProveedor] = useState<{
    id: string;
    rut: string | null;
    razon: string;
  } | null>(null);
  const proveedor = proveedorFijo ?? elegido;
  const [buscando, setBuscando] = useState(false);
  const [desde, setDesde] = useState(hoy());
  const [motivo, setMotivo] = useState('');
  const [editados, setEditados] = useState<Record<string, string>>({});
  const [porCaja, setPorCaja] = useState(true);
  const [pegado, setPegado] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const puedeEditar = puede('compras.politica.gestionar');

  const filas = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'costos-logisticos', proveedor?.id ?? ''],
    queryFn: () =>
      api.get<FilaCosto[]>('/compras/costos-logisticos', {
        query: { empresaId, proveedorEntidadId: proveedor!.id },
      }),
    enabled: !!proveedor,
  });
  const estrategia = useQuery({
    queryKey: ['emp', empresaId, 'compras', 'estrategia-flete', proveedor?.id ?? ''],
    queryFn: () =>
      api.get<{ estrategia: { estrategia: string; codigo: string } | null }>(
        '/compras/costos-logisticos/estrategia',
        { query: { empresaId, proveedorEntidadId: proveedor!.id } },
      ),
    enabled: !!proveedor,
  });
  const cobroActual: Cobro =
    (estrategia.data?.estrategia?.estrategia as Cobro | undefined) ?? 'EN_LINEA';
  useEffect(() => {
    setEditados({});
    setAviso(null);
  }, [proveedor?.id]);

  const factor = (f: FilaCosto) => (porCaja ? Number(f.factorABase ?? 1) || 1 : 1);
  const valorDe = (f: FilaCosto) =>
    editados[f.productoId] ??
    (f.montoBase !== null ? String(Math.round(Number(f.montoBase) * factor(f) * 100) / 100) : '');
  const cambios = useMemo(
    () =>
      (filas.data ?? []).filter((f) => {
        const v = editados[f.productoId];
        if (v === undefined) return false;
        const base = estimar(v) / factor(f);
        return f.montoBase === null
          ? v.trim() !== ''
          : Math.abs(base - Number(f.montoBase)) > 0.00005;
      }),
    [filas.data, editados, porCaja],
  );

  const guardar = () => {
    const filasApi = cambios.map((f) => ({
      productoId: f.productoId,
      montoBase: String(Number((estimar(editados[f.productoId]!) / factor(f)).toFixed(4))),
    }));
    accion.mutate(
      {
        ruta: '/compras/costos-logisticos',
        cuerpo: {
          empresaId,
          proveedorEntidadId: proveedor!.id,
          vigenteDesde: desde,
          motivo: motivo.trim(),
          filas: filasApi,
        },
      },
      {
        onSuccess: () => {
          setEditados({});
          setAviso(
            `Guardados ${filasApi.length} costos logísticos, vigentes desde ${fecha(desde)}.`,
          );
          void qc.invalidateQueries({
            queryKey: ['emp', empresaId, 'compras', 'costos-logisticos'],
          });
        },
      },
    );
  };

  const cambiarCobro = (c: Cobro) => {
    if (!proveedor || c === cobroActual) return;
    accion.mutate(
      {
        ruta: '/compras/politicas',
        cuerpo: {
          politica: {
            empresaId,
            codigo: codigoPolitica(proveedor.rut, proveedor.id),
            ambito: 'FLETE',
            prioridad: 10,
            activa: c !== 'EN_LINEA',
            condicion: {
              proveedorEntidadIds: [proveedor.id],
              estrategia: c === 'EN_LINEA' ? 'FLETE_POR_VALOR' : c,
            },
            requiereAprobacion: false,
            descripcion: `Flete de ${proveedor.razon}`.slice(0, 300),
          },
          motivo: `Cómo cobra el flete ${proveedor.razon}`.slice(0, 500),
        },
      },
      {
        onSuccess: () =>
          void qc.invalidateQueries({
            queryKey: ['emp', empresaId, 'compras', 'estrategia-flete'],
          }),
      },
    );
  };

  /** Pegar desde Excel: «código<TAB>costo» por fila (el costo, por caja o por unidad según el switch). */
  const aplicarPegado = () => {
    const porSku = new Map((filas.data ?? []).map((f) => [(f.sku ?? '').toUpperCase(), f]));
    const nuevos: Record<string, string> = {};
    const noEncontrados: string[] = [];
    for (const linea of pegado.split(/\r?\n/)) {
      const [cod, valor] = linea.split(/\t|;/).map((x) => x.trim());
      if (!cod || !valor) continue;
      const f = porSku.get(cod.toUpperCase());
      if (f) nuevos[f.productoId] = valor.replace(/\./g, '').replace(',', '.');
      else noEncontrados.push(cod);
    }
    setEditados((e) => ({ ...e, ...nuevos }));
    setPegado('');
    setAviso(
      `${Object.keys(nuevos).length} costos tomados de lo pegado${noEncontrados.length ? `; sin producto: ${noEncontrados.slice(0, 8).join(', ')}${noEncontrados.length > 8 ? '…' : ''}` : ''}.`,
    );
  };

  return (
    <div className="pantalla consulta" data-pantalla="costos-logisticos">
      <p className="apagado">
        Cómo cobra el flete cada proveedor y, si lo cobra en un total, el costo logístico de cada
        producto para repartirlo bien (pídeselo al proveedor). Con eso, el costo de cada producto
        incluye su flete justo.
      </p>
      {!proveedorFijo && (
        <div className="barra-filtros">
          <div className="campo">
            <span className="etiqueta">Proveedor</span>
            <button
              type="button"
              className="selector-cliente"
              aria-label="Elegir proveedor"
              onClick={() => setBuscando(true)}
            >
              {proveedor ? proveedor.razon : 'Buscar proveedor…'}
            </button>
          </div>
        </div>
      )}
      {accion.isError && <PanelError error={accion.error} />}
      {aviso && <p className="tono-ok">{aviso}</p>}

      {proveedor && (
        <>
          <section className="paso-cobro" aria-label="Cómo cobra el flete">
            <span className="etiqueta">¿Cómo cobra el flete {proveedor.razon}?</span>
            <div className="segmentos" role="radiogroup" aria-label="Cómo cobra el flete">
              {COBROS.map(([v, t, ayuda]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={cobroActual === v}
                  className={cobroActual === v ? 'activo' : ''}
                  title={ayuda}
                  disabled={!puedeEditar || accion.isPending}
                  onClick={() => cambiarCobro(v)}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="ayuda">{COBROS.find((c) => c[0] === cobroActual)?.[2]}</p>
          </section>

          <section className="paso-cobro" aria-label="Costos logísticos por producto">
            <div className="barra-filtros">
              <label className="opcion-radio">
                <input
                  type="checkbox"
                  checked={porCaja}
                  onChange={(e) => {
                    setPorCaja(e.target.checked);
                    setEditados({});
                  }}
                />
                Digitar por caja (la presentación con que se compra)
              </label>
              <label className="campo">
                <span className="etiqueta">Vigente desde</span>
                <input
                  type="date"
                  aria-label="Vigente desde"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value || hoy())}
                />
              </label>
            </div>
            {filas.isError && <PanelError error={filas.error} />}
            {filas.isLoading && <p className="apagado">Cargando…</p>}
            {filas.isSuccess && !filas.data.length && (
              <p className="apagado">
                Todavía no hay compras de este proveedor: los productos aparecen al registrar su
                primera factura.
              </p>
            )}
            {!!filas.data?.length && (
              <table className="grilla compacta" aria-label="Costos logísticos">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Unid.</th>
                    <th className="der">
                      Costo logístico {porCaja ? '(por caja)' : '(por unidad)'}
                    </th>
                    <th className="der">Por unidad</th>
                    <th>Vigente desde</th>
                    <th>Última compra</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.data.map((f) => {
                    const v = valorDe(f);
                    const base = v ? estimar(v) / factor(f) : null;
                    return (
                      <tr
                        key={f.productoId}
                        className={editados[f.productoId] !== undefined ? 'con-abono' : ''}
                      >
                        <td className="mono">{f.sku ?? '—'}</td>
                        <td>{f.descripcion ?? '—'}</td>
                        <td>
                          {porCaja && f.presentacionCodigo
                            ? `${f.presentacionCodigo}${Number(f.factorABase ?? 1) !== 1 ? ` ×${decimal(f.factorABase)}` : ''}`
                            : 'UN'}
                        </td>
                        <td className="der">
                          <input
                            className="celda-num"
                            inputMode="decimal"
                            aria-label={`Costo logístico ${f.sku ?? f.productoId}`}
                            disabled={!puedeEditar}
                            value={v}
                            onChange={(e) =>
                              setEditados((x) => ({
                                ...x,
                                [f.productoId]: e.target.value.replace(',', '.'),
                              }))
                            }
                          />
                        </td>
                        <td className="der mono">
                          {base !== null ? pesos(String(Math.round(base * 100) / 100)) : ''}
                        </td>
                        <td>
                          {f.vigenteDesde ? fecha(f.vigenteDesde) : '—'}
                          {f.motivo && <div className="apagado chico">{f.motivo}</div>}
                        </td>
                        <td>{f.ultimaCompra ? fecha(f.ultimaCompra) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {puedeEditar && !!filas.data?.length && (
              <>
                <details>
                  <summary>Pegar desde Excel (código y costo)</summary>
                  <textarea
                    aria-label="Pegar desde Excel"
                    rows={5}
                    placeholder={'CERVEZA-330\t480\nAGUA-500\t120'}
                    value={pegado}
                    onChange={(e) => setPegado(e.target.value)}
                  />
                  <button type="button" disabled={!pegado.trim()} onClick={aplicarPegado}>
                    Tomar lo pegado
                  </button>
                </details>
                <div className="acciones-od">
                  <label className="campo">
                    <span className="etiqueta">Motivo</span>
                    <input
                      aria-label="Motivo"
                      placeholder="Ej.: lista de costos logísticos de CCU octubre"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="primario"
                    disabled={!cambios.length || motivo.trim().length < 3 || accion.isPending}
                    onClick={guardar}
                  >
                    Guardar {cambios.length || ''} costo{cambios.length === 1 ? '' : 's'}
                  </button>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {buscando && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({ id: c.id, rut: c.rut, razon: c.razon_social });
            setBuscando(false);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}
